import { Icon } from './Icon';

export type NoticeData =
  | { kind: 'paused'; message?: string; onRetry?: () => void }
  | { kind: 'unavailable'; message?: string; onRetry?: () => void }
  | { kind: 'rate-limited'; retryAfter?: number; message?: string; onRetry?: () => void }
  | { kind: 'forbidden'; message?: string }
  | { kind: 'unauthorized'; message?: string }
  | { kind: 'error'; message: string; onRetry?: () => void };

const SURFACES: Record<NoticeData['kind'], { bg: string; border: string }> = {
  paused:         { bg: 'bg-primary-soft', border: 'border-[color:var(--color-primary)]' },
  unavailable:    { bg: 'bg-warning-soft', border: 'border-[color:var(--color-warning)]' },
  'rate-limited': { bg: 'bg-warning-soft', border: 'border-[color:var(--color-warning)]' },
  forbidden:      { bg: 'bg-warning-soft', border: 'border-[color:var(--color-warning)]' },
  unauthorized:   { bg: 'bg-warning-soft', border: 'border-[color:var(--color-warning)]' },
  error:          { bg: 'bg-danger-soft',  border: 'border-[color:var(--color-danger)]'  },
};

function renderHeader(data: NoticeData): { icon: string; title: string; message: string } {
  switch (data.kind) {
    case 'paused':
      return {
        icon: '⏸',
        title: 'Kairos is paused',
        message: data.message || 'Kairos is currently paused by operators. Try again shortly.',
      };
    case 'unavailable':
      return {
        icon: '⚠️',
        title: 'Kairos is temporarily unavailable',
        message: data.message || 'The service is having trouble responding. Try again in a moment.',
      };
    case 'rate-limited':
      return {
        icon: '🐢',
        title: 'Slow down a moment',
        message:
          data.retryAfter != null
            ? `Rate limit reached. Try again in about ${data.retryAfter} second${data.retryAfter === 1 ? '' : 's'}.`
            : data.message || 'Rate limit reached. Try again shortly.',
      };
    case 'forbidden':
      return {
        icon: '🔒',
        title: 'No access',
        message: data.message || "You don't have permission to use Kairos. Ask a Founder to grant the Kairos role.",
      };
    case 'unauthorized':
      return {
        icon: '🔑',
        title: 'Session expired',
        message: data.message || 'Please reload the page to sign in again.',
      };
    case 'error':
      return {
        icon: 'i-warning',
        title: 'Something went wrong',
        message: data.message,
      };
  }
}

export function Notice(data: NoticeData) {
  const { icon, title, message } = renderHeader(data);
  const onRetry = 'onRetry' in data ? data.onRetry : undefined;
  const surface = SURFACES[data.kind];
  return (
    <div
      className={
        `border rounded-lg p-4 my-2 flex gap-3 items-start ${surface.bg} ${surface.border} border-opacity-30 text-text`
      }
    >
      <span className="text-lg leading-none flex-shrink-0 text-warning" aria-hidden="true">
        {icon.startsWith('i-') ? <Icon name={icon} size={20} /> : icon}
      </span>
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className="text-sm mt-0.5">{message}</div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-2 px-3 py-1 bg-primary hover:bg-primary-hover text-white rounded text-sm"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
